import { slugify } from "@prismicio/editor-support/String"
import {
  collectWidgets,
  Document as DocumentT,
  type FieldContent,
  type RichTextContent
} from "@prismicio/types-internal/lib/content"

export function extractRelevantTextFromContent(
  content: DocumentT
): string | undefined {
  // Try to find a heading with content, by order of importance
  for (const headingType of headingTypePriority) {
    const headingValue = getRichTextContentsByType(content, headingType)

    if (headingValue.length > 0) return headingValue[0]
  }

  // If no headings were found, find keytext fields that can do this same
  const keyTexts = collectWidgets(
    content,
    (widget): widget is FieldContent =>
      widget.__TYPE__ === "FieldContent" && widget.type === "Text"
  )

  const keyTextValues = Object.values(keyTexts).map((content) => content.value)

  // If no keytexts have content, then leave the UID undefined
  if (keyTextValues.length === 0) return

  return keyTextValues[0]
}

const headingTypePriority = [
  "heading1",
  "heading2",
  "heading3",
  "heading4",
  "heading5",
  "heading6",
  "paragraph"
] as const

type HeadingType = typeof headingTypePriority[number]

export function getRichTextContentsByType(
  content: DocumentT,
  type: HeadingType
) {
  const richTexts = collectWidgets(
    content,
    (widget): widget is RichTextContent =>
      widget.__TYPE__ === "StructuredTextContent"
  )

  const richTextsValues = Object.values(richTexts).reduce<string[]>(
    (acc, content) => {
      const contentValues = getContentTextValues(content.value, type)

      return [...acc, ...contentValues]
    },
    []
  )

  return richTextsValues
}

function getContentTextValues(
  richTextValue: RichTextContent["value"],
  textType: HeadingType
) {
  const contentValues = richTextValue.reduce<string[]>(
    (contentValues, contentValue) => {
      if (contentValue.type !== textType) return contentValues

      return [...contentValues, contentValue.content.text]
    },
    []
  )

  return contentValues
}

export function setGeneratedUID(content: DocumentT): DocumentT {
  const generatedUID = extractRelevantTextFromContent(content)
  if (!generatedUID) return content

  return {
    ...content,
    uid: {
      __TYPE__: "UIDContent",
      value: slugify(generatedUID).substring(0, 50)
    }
  }
}
